import React from 'react';
import { Safe, Caduceus, Injection, ICON_SIZES } from './icons';
import { useUspSection } from '../hooks/useUspSection';
import { getCurrentLocale } from '../services/api';

// Icon mapping for USPs
const iconMap = {
  SafeIcon: Safe,
  CaduceusIcon: Caduceus,
  InjectionIcon: Injection
};

interface UspItem {
  title: string;
  description: string;
  icon: 'SafeIcon' | 'CaduceusIcon' | 'InjectionIcon';
}

interface UspSectionProps {
  title?: string;
  description?: string;
  usp1?: UspItem;
  usp2?: UspItem;
  usp3?: UspItem;
  useStrapi?: boolean;
}

const UspSection: React.FC<UspSectionProps> = ({ 
  title, 
  description, 
  usp1, 
  usp2, 
  usp3, 
  useStrapi = true // Default to true to use Strapi data
}) => {
  const { data: strapiData, loading, error } = useUspSection();
  const locale = getCurrentLocale();

  // Debug logging for USP section data
  if (strapiData && !loading && !error) {
    console.log('🎯 UspSection - ✅ Strapi data loaded:', !!strapiData);
    console.log('🎯 UspSection - Data keys:', Object.keys(strapiData || {}));
    console.log('🎯 UspSection - Title:', (strapiData as any).title);
    console.log('🎯 UspSection - USP1:', (strapiData as any).usp1?.title);
    console.log('🎯 UspSection - USP2:', (strapiData as any).usp2?.title);
    console.log('🎯 UspSection - USP3:', (strapiData as any).usp3?.title);
  }

  if (loading) {
    console.log('🎯 UspSection - Loading Strapi data...');
  }

  if (error) {
    console.log('🎯 UspSection - ❌ Error loading Strapi data:', error);
    console.log('🎯 UspSection - Falling back to props or default data');
  }

  // Respect useStrapi flag: when false, prefer props even if Strapi data exists
  const data = (useStrapi && strapiData) ? strapiData : { title, description, usp1, usp2, usp3 };

  if (loading) {
    return (
      <section className="usp-section" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="usp-container">Loading USP section...</div>
      </section>
    );
  }

  // Default content when no data is available
  const defaultContent = {
    title: "Gespecialiseerd in haarverlies en hoofdhuidproblemen bij mannen én vrouwen",
    description: "Bij HairMedics geloven we dat haarverlies invloed heeft op hoe je je voelt. Daarom combineren we medische kennis met persoonlijke aandacht, voor mannen en vrouwen die klaar zijn met vage beloftes en iets willen dat écht werkt.",
    usp1: {
      title: "Medisch verantwoord",
      description: "Behandelingen op basis van wetenschap en trichologie.",
      icon: "SafeIcon" as const
    },
    usp2: {
      title: "Volledige discretie",
      description: "Geen wachtruimte, volle privacy, behandeling in rust.",
      icon: "CaduceusIcon" as const
    },
    usp3: {
      title: "Toegankelijke methode",
      description: "Zonder operatie, past in je agenda en je leven.",
      icon: "InjectionIcon" as const
    }
  };

  // Use data with field-level fallbacks for better control
  const content = {
    title: data?.title || defaultContent.title,
    description: data?.description || defaultContent.description,
    usp1: data?.usp1 || defaultContent.usp1,
    usp2: data?.usp2 || defaultContent.usp2,
    usp3: data?.usp3 || defaultContent.usp3,
  };
  
  const features = [content.usp1, content.usp2, content.usp3].filter(Boolean);

  return (
    <section className="usp-section" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="usp-container">
        {content.title && (
          <h2 className="usp-title">
            {content.title}
          </h2>
        )}
        
        {content.description && (
          <p className="usp-description">
            {content.description}
          </p>
        )}
        
        {/* USPs grid */}
        <div className="usp-grid">
          {features.map((feature, index) => {
            // Ensure feature exists and has an icon property
            if (!feature || !feature.icon) {
              console.warn('Feature missing or has no icon:', feature);
              return null;
            }
            
            const IconComponent = iconMap[feature.icon] || Safe; // Fallback to Safe icon
            return (
              <div key={index} className="usp-item">
                <IconComponent 
                  size={ICON_SIZES.xxl}
                  className="text-dark-blue"
                />
                <h3 className="usp-item-title">
                  {feature.title}
                </h3>
                <p className="usp-item-description">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UspSection;
